import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Gynecology = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Actual gynecology products based on the images in public/product_img/GYNAECOLOGY
  const products = [
    {
      id: 'calijoy-hd',
      name: 'CALIJOY-HD',
      description: 'Advanced calcium supplement with Vitamin D3 for women, supporting bone health during and after pregnancy.',
      dosageForm: 'Tablets',
      category: 'Nutritional',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/CALIJOY-HD.png'
    },
    {
      id: 'calijoy',
      name: 'CALIJOY',
      description: 'Essential calcium supplement formulated for women\'s unique nutritional needs.',
      dosageForm: 'Tablets',
      category: 'Nutritional',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/CALIJOY.png'
    },
    {
      id: 'safenine-gold',
      name: 'SAFENINE GOLD',
      description: 'Premium prenatal injection supplement for expectant mothers, ensuring optimal nutrition for both mother and child.',
      dosageForm: 'Injection',
      category: 'Prenatal',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/INJECTION%20SAFENINE%20GOLD.png'
    },
    {
      id: 'joy-d3-nano',
      name: 'JOY-D3 NANO SHOT',
      description: 'Innovative nano-formulation Vitamin D3 supplement for enhanced absorption and efficacy.',
      dosageForm: 'Oral Shot',
      category: 'Nutritional',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/JOY-D3%20NANO%20SHOT.png'
    },
    {
      id: 'miglini-plus',
      name: 'MIGLINI PLUS GEL',
      description: 'Specialized topical gel for women\'s intimate health and comfort.',
      dosageForm: 'Gel',
      category: 'Topical',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/MIGLINI%20PLUS%20GEL.png'
    },
    {
      id: 'rabismall-dsr',
      name: 'RABISMALL-DSR',
      description: 'Dual-action formula for managing gastric symptoms during pregnancy.',
      dosageForm: 'Capsules',
      category: 'Gastric',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/RABISMALL-DSR.png'
    },
    {
      id: 'redsmart',
      name: 'REDSMART',
      description: 'Iron supplement specifically formulated to address iron deficiency anemia in women.',
      dosageForm: 'Tablets',
      category: 'Hematological',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/REDSMART.png'
    },
    {
      id: 'safenine-bio',
      name: 'SAFENINE BIO',
      description: 'Bioavailable prenatal supplement with essential nutrients for fetal development.',
      dosageForm: 'Tablets',
      category: 'Prenatal',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/SAFENINE%20BIO.png'
    },
    {
      id: 'safenine-dha',
      name: 'SAFENINE DHA',
      description: 'Prenatal supplement with DHA for optimal fetal brain and eye development.',
      dosageForm: 'Capsules',
      category: 'Prenatal',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/SAFENINE%20DHA.png'
    },
    {
      id: 'safenine',
      name: 'SAFENINE',
      description: 'Comprehensive prenatal vitamin and mineral supplement for expectant mothers.',
      dosageForm: 'Tablets',
      category: 'Prenatal',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/SAFENINE.png'
    },
    {
      id: 'ortho',
      name: 'ORTHO',
      description: 'Specialized orthopedic supplement for women with bone and joint concerns.',
      dosageForm: 'Tablets',
      category: 'Orthopedic',
      image: process.env.PUBLIC_URL + '/product_img/GYNAECOLOGY/ORTHO.png'
    }
  ];

  const [selectedProduct, setSelectedProduct] = React.useState(null);

  const openProductDetails = (product) => {
    setSelectedProduct(product);
  };

  const closeProductDetails = () => {
    setSelectedProduct(null);
  };

  return (
    <div className="pt-20">
      {/* Header */}
      <div className="bg-gynecology text-white py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center mb-4">
              <Link to="/products" className="flex items-center text-white hover:text-gynecology-light transition-colors">
                <FaArrowLeft className="mr-2" /> Back to Categories
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Gynecology Products</h1>
            <p className="text-xl text-gray-100 max-w-3xl mb-8">
              Our comprehensive range of women's health products addressing reproductive wellness, 
              hormonal balance, and prenatal care.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Products List */}
      <div className="container-custom py-20">
        <motion.div
          variants={container}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              variants={fadeIn}
              className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <div className="h-64 bg-gynecology/5 flex items-center justify-center p-4">
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-gynecology">{product.name}</h3>
                  <span className="bg-gynecology/10 text-gynecology text-xs px-2 py-1 rounded-full">
                    {product.dosageForm}
                  </span>
                </div>
                <p className="text-gray-600 mb-4">
                  {product.description}
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-500">
                    {product.category}
                  </span>
                  <button 
                    onClick={() => openProductDetails(product)} 
                    className="text-gynecology font-medium hover:underline flex items-center"
                  >
                    View Details <FaInfoCircle className="ml-2" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto"
          >
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gynecology">{selectedProduct.name}</h2>
                <button 
                  onClick={closeProductDetails}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-center">
                  <img 
                    src={selectedProduct.image} 
                    alt={selectedProduct.name} 
                    className="max-w-full max-h-80 object-contain"
                  />
                </div>
                
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2">Description</h3>
                    <p className="text-gray-600">{selectedProduct.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Category</h3>
                      <p className="text-gray-600">{selectedProduct.category}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Dosage Form</h3>
                      <p className="text-gray-600">{selectedProduct.dosageForm}</p>
                    </div>
                  </div>
                  
                  <div className="bg-gynecology/5 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Key Benefits</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      {selectedProduct.category === 'Prenatal' && (
                        <>
                          <li>Supports healthy fetal development</li>
                          <li>Provides essential nutrients for expectant mothers</li>
                          <li>Formulated for optimal absorption</li>
                        </>
                      )}
                      {selectedProduct.category === 'Nutritional' && (
                        <>
                          <li>Supports bone health and strength</li>
                          <li>Helps maintain optimal calcium levels</li>
                          <li>Prevents nutritional deficiencies</li>
                        </>
                      )}
                      {selectedProduct.category === 'Hematological' && (
                        <>
                          <li>Helps address iron deficiency anemia</li>
                          <li>Supports healthy red blood cell production</li>
                          <li>Improves energy levels and reduces fatigue</li>
                        </>
                      )}
                      {selectedProduct.category === 'Topical' && (
                        <>
                          <li>Provides soothing relief</li>
                          <li>Maintains optimal pH balance</li>
                          <li>Gentle formulation for sensitive areas</li>
                        </>
                      )}
                      {selectedProduct.category === 'Gastric' && (
                        <>
                          <li>Relieves acid reflux and indigestion</li>
                          <li>Safe for use during pregnancy</li>
                          <li>Provides fast-acting relief</li>
                        </>
                      )}
                      {selectedProduct.category === 'Orthopedic' && (
                        <>
                          <li>Supports bone and joint health</li>
                          <li>Helps maintain optimal bone density</li>
                          <li>Reduces risk of osteoporosis</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  * Consult with a healthcare professional before starting any new medication or supplement.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Gynecology;
